<template>
  <div
    class="library-link-cell"
    data-test-id="library-link-cell"
  >
    <q-btn
      class="q-py-none"
      style="min-height: 22px;"
      dense
      outline
      :disable="dataLoading || collection.isTranslation || !libraryLinkModel.isManagePermitted"
      @click.stop="() => {}"
    >
      <span
        style="font-size: 12px; text-transform: none; white-space: nowrap;"
      >
        <q-icon
          :name="cellContent.icon"
          :color="cellContent.color"
          size="12px"
        />
        {{ cellContent.label }}
      </span>

      <action-not-permitted-tooltip
        v-if="!collection.isManagePermitted"
      />

      <q-menu
        ref="libraryLinkCellMenu"
        style="max-width: 300px"
        anchor="bottom left"
        self="top start"
        :offset="[0, 5]"
        @before-show="reset"
        @hide="reset"
      >
        <q-list
          style="max-width: 300px; box-shadow: 0 2px 0 0 rgba(10, 13, 18, 0.04);"
        >
          <q-item
            class="word-break text-body-em"
            style="min-height: 36px; font-size: 16px;"
          >
            <q-item-section>
              <q-item-label class="row no-wrap q-gutter-x-xs">
                <q-icon
                  class="col-auto"
                  name="far fa-book-open"
                  color="grey"
                />
                <span
                  class="col ellipsis text-grey-10"
                  style="font-weight: bold; white-space: nowrap; min-width: 0;"
                >
                  {{ libraryLinkModel.name }}
                </span>
                <span
                  v-if="hasLibraryParent"
                  class="col-auto text-grey-8"
                >
                  LIB ID {{ libraryLinkModel.libraryParentId }}
                </span>
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-separator
            class="q-my-xs"
          />
          <q-item
            class="q-px-lg"
          >
            <q-item-section
              class="items-center justify-center"
            >
              <q-btn
                class="full-width text-body-em rounded-borders-8"
                style="border-color: #D5D7DA; box-shadow: 0 1px 0 0 rgba(213, 215, 218, 0.5);"
                outline
                dense
                :disable="!hasLibraryParent || dataLoading"
                :loading="dataLoading"
                @click="forceUpdate"
              >
                <span
                  style="font-size: 14px; text-transform: none; white-space: nowrap;"
                >
                  <q-icon
                    name="far fa-arrows-rotate"
                    size="14px"
                  />
                  Force Update
                </span>
                <q-tooltip
                  v-if="!hasLibraryParent"
                  class="text-caption word-break"
                >
                  Link this {{ libraryLinkModel.humanTypeName }} to the Library to trigger updates.
                </q-tooltip>
              </q-btn>
              <q-item-label
                class="text-caption word-break q-mt-xs"
                style="font-size: 12px;"
                :style="{ opacity: !hasLibraryParent || dataLoading ? 0.25 : undefined }"
              >
                Force sync all content from the linked Library {{ libraryLinkModel.humanTypeName }},
                to ensure everything is up to date.
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-separator
            class="q-my-xs"
          />
          <q-item
            class="q-pa-xs"
          >
            <q-item-section>
              <q-radio
                :model-value="libraryLinkAction"
                val="edit-link"
                size="xs"
                :disable="dataLoading"
                @update:model-value="editLibraryLink"
              >
                <div>
                  <span class="radio-label text-body-em">
                    <q-icon
                      class="q-mr-xs"
                      :name="linkContent.icon"
                      color="grey"
                    />
                    {{ linkContent.label }}
                  </span>
                </div>
              </q-radio>
              <q-item-label
                class="radio-description text-caption word-break"
                :style="{ opacity: dataLoading ? 0.25 : undefined }"
              >
                {{ linkContent.tooltip }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-item
            class="q-pa-xs"
          >
            <q-item-section>
              <q-radio
                :model-value="libraryLinkAction"
                val="enable-updates"
                size="xs"
                :disable="collection.fields.receiveLibraryUpdates.output || !hasLibraryParent || dataLoading"
                @update:model-value="enableLibraryUpdates"
              >
                <div>
                  <span class="radio-label text-body-em">
                    <q-icon
                      class="q-mr-xs"
                      name="fa fa-link-horizontal"
                      color="grey"
                    />
                    Enable Updates
                  </span>
                </div>
              </q-radio>
              <q-item-label
                class="radio-description text-caption word-break"
                :style="{ opacity: !hasLibraryParent || dataLoading ? 0.25 : undefined }"
              >
                Stay linked to the Library and receive automatic updates.
              </q-item-label>
              <q-tooltip
                v-if="collection.fields.receiveLibraryUpdates.output"
                class="text-caption word-break"
              >
                Control {{ libraryLinkModel.humanTypeName }} updates from it's {{ collection.parent.humanTypeName }}.
              </q-tooltip>
              <q-tooltip
                v-else-if="!hasLibraryParent"
                class="text-caption word-break"
              >
                Link this {{ libraryLinkModel.humanTypeName }} to the Library to configure automatic updates.
              </q-tooltip>
            </q-item-section>
          </q-item>
          <q-item
            class="q-pa-xs"
          >
            <q-item-section>
              <q-radio
                :model-value="libraryLinkAction"
                val="disable-updates"
                size="xs"
                :disable="collection.fields.receiveLibraryUpdates.output || !hasLibraryParent || dataLoading"
                @update:model-value="disableLibraryUpdates"
              >
                <div>
                  <span class="radio-label text-body-em">
                    <q-icon
                      class="q-mr-xs"
                      name="fa fa-link-horizontal-slash"
                      color="grey"
                    />
                    Disable Updates
                  </span>
                </div>
              </q-radio>
              <q-item-label
                class="radio-description text-caption word-break"
                :style="{ opacity: !hasLibraryParent || dataLoading ? 0.25 : undefined }"
              >
                Stay linked to the Library but without automatic updates.
              </q-item-label>
              <q-tooltip
                v-if="collection.fields.receiveLibraryUpdates.output"
                class="text-caption word-break"
              >
                Control {{ libraryLinkModel.humanTypeName }} updates from it's {{ collection.parent.humanTypeName }}.
              </q-tooltip>
              <q-tooltip
                v-else-if="!hasLibraryParent"
                class="text-caption word-break"
              >
                Link this {{ libraryLinkModel.humanTypeName }} to the Library to configure automatic updates.
              </q-tooltip>
            </q-item-section>
          </q-item>
          <q-item
            class="q-pa-none"
            style="min-height: 36px"
          >
            <q-item-section
              class="items-end justify-end"
            >
              <q-btn
                style="text-transform: none; font-size: 14px;"
                label="Save changes"
                color="primary"
                flat
                :loading="dataLoading"
                :disable="!canSaveChanges"
                @click="save"
              >
                <q-tooltip
                  v-if="!canSaveChanges && !hasLibraryParent"
                  class="text-caption word-break"
                >
                  Select a Library {{ libraryLinkModel.humanTypeName }} to link this {{ collection.libraryLinkModel }} to before saving changes.
                </q-tooltip>
                <q-tooltip
                  v-else-if="!canSaveChanges"
                  class="text-caption word-break"
                >
                  Select a new Library option before saving changes.
                </q-tooltip>
              </q-btn>
            </q-item-section>
          </q-item>
        </q-list>

        <q-menu
          :model-value="!hasLibraryParent && updateLibraryParent"
          style="width: 300px;"
          anchor="top start"
          self="top end"
          :offset="[5, 0]"
          no-parent-event
        >
          <q-list
            style="max-width: 300px; box-shadow: 0 2px 0 0 rgba(10, 13, 18, 0.04);"
          >
            <q-item
              class="library-list-header word-break text-body-em text-grey-8"
              style="min-height: 36px; font-size: 16px;"
            >
              <q-item-section>
                <q-item-label>
                  Link <span
                    class="text-grey-10"
                    style="font-weight: bold;"
                  >{{ libraryLinkModel.name }}</span> to:
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-separator />
            <q-item
              v-if="!$_.isNil(libraryFilter)"
            >
              <q-item-section>
                <q-input
                  :model-value="librarySearchString"
                  class="rounded-borders-8"
                  input-class="text-body-em text-grey-10"
                  color="primary"
                  debounce="300"
                  dense
                  clearable
                  @clear="() => updateLibrarySearchString('')"
                  @update:model-value="updateLibrarySearchString"
                >
                  <template #prepend>
                    <q-icon
                      name="fas fa-magnifying-glass"
                    />
                  </template>
                </q-input>
              </q-item-section>
            </q-item>
            <q-separator />
            <q-list
              class="q-pb-xs"
              style="max-height: 320px; overflow-y: auto;"
            >
              <q-item
                v-if="canCreateLibraryModel"
                class="q-pr-xs q-py-none"
                :class="{ 'bg-responsive-grey-2': libraryParentId === CREATE_NEW_LIBRARY_MODEL_ID }"
                :disable="dataLoading"
                clickable
                @click="() => updateLibraryLinkModel(CREATE_NEW_LIBRARY_MODEL_ID)"
              >
                <q-item-section>
                  <q-item-label
                    class="row no-wrap text-body-em items-center q-gutter-x-xs"
                    style="font-size: 14px;"
                  >
                    <div class="column col">
                      <span
                        class="ellipsis col text-grey-10"
                        style="font-weight: bold; white-space: nowrap; min-width: 0;"
                      >
                        Create New Library {{ libraryLinkModel.humanTypeName }}
                      </span>
                      <span
                        class="text-caption text-grey-8"
                        style="font-size: 12px; line-height: 1.2em;"
                      >
                        Use this if no suitable match exists.
                      </span>
                    </div>
                    <q-icon
                      v-if="libraryParentId === CREATE_NEW_LIBRARY_MODEL_ID"
                      class="col-auto"
                      name="fas fa-check"
                      size="14px"
                      color="primary"
                    />
                  </q-item-label>
                </q-item-section>
              </q-item>
              <template
                v-for="libraryModel in libraryOptions"
                :key="libraryModel.id"
              >
                <q-item
                  class="q-pr-xs q-py-none"
                  :class="{ 'bg-responsive-grey-2': libraryParentId === libraryModel.id }"
                  style="max-height: 40px;"
                  :disable="dataLoading"
                  clickable
                  @click="() => updateLibraryLinkModel(libraryModel.id)"
                >
                  <q-item-section>
                    <q-item-label
                      class="row no-wrap text-body-em items-center q-gutter-x-xs"
                      style="font-size: 14px;"
                    >
                      <span
                        class="ellipsis col text-grey-10"
                        style="font-weight: bold; white-space: nowrap; min-width: 0;"
                      >
                        {{ libraryModel.name || libraryModel.title || libraryModel.displayName }}
                      </span>
                      <span
                        class="col-auto text-grey-8"
                        style="white-space: nowrap;"
                      >
                        ID {{ libraryModel.id }}
                      </span>
                      <q-icon
                        class="col-auto"
                        style="min-width: 14px;"
                        :name="libraryParentId === libraryModel.id ? 'fas fa-check' : undefined"
                        size="14px"
                        color="primary"
                      />
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
              <q-item
                v-if="!canCreateLibraryModel && $_.isEmpty(libraryOptions)"
                class="q-pr-xs q-py-none"
                disable
              >
                <q-item-section>
                  <q-item-label
                    class="row no-wrap text-body-em items-center q-gutter-x-xs"
                    style="font-size: 14px;"
                  >
                    <div class="column col">
                      <span
                        class="ellipsis col text-grey-10"
                        style="font-weight: bold; white-space: nowrap; min-width: 0;"
                      >
                        No matching Library {{ libraryLinkModel.humanCollectionName }}
                      </span>
                      <span
                        v-if="!$_.isEmpty(librarySearchString)"
                        class="text-caption text-grey-8"
                        style="font-size: 12px; line-height: 1.2em;"
                      >
                        Try using a different search term.
                      </span>
                    </div>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-list>
        </q-menu>
      </q-menu>
    </q-btn>
  </div>
</template>

<script>
import { MODEL_LEVEL } from 'src/models/model'
import { defineAsyncComponent } from 'vue'

const CREATE_NEW_LIBRARY_MODEL_ID = -1

export default {
  name: 'LibraryLinkCell',
  configuration: ['currentSite', 'currentCircuit'],
  context: ['currentUser'],
  components: {
    ActionNotPermittedTooltip: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ActionNotPermittedTooltip'))
  },
  props: {
    props: {
      type: Object,
      required: true
    },
    collection: {
      type: Object,
      required: true
    },
    libraryOptions: {
      type: Array,
      required: true
    },
    libraryFilter: {
      type: Function,
      required: false,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      librarySearchString: '',
      updateLibraryParent: false,
      libraryParentId: null,
      updateReceiveLibraryUpdates: false,
      receiveLibraryUpdates: false
    }
  },
  computed: {
    libraryLinkModel () {
      // props.value is used for Pages/Sections, as the library link controls an associated model
      if (!_.isNil(this.props?.value)) return this.props.value

      return this.props.row
    },
    libraryLinkModelName () {
      return this.libraryLinkModel.name ||
        this.libraryLinkModel.title ||
        this.libraryLinkModel.displayName
    },
    hasLibraryParent () {
      return !_.isNil(this.libraryLinkModel.libraryParentId)
    },
    cellContent () {
      if (!this.hasLibraryParent) {
        return {
          label: 'Not Linked',
          icon: 'far fa-burst',
          color: 'negative'
        }
      } else if (this.libraryLinkModel.receiveLibraryUpdates) {
        return {
          label: 'Linked with Updates',
          icon: 'fa fa-badge-check',
          color: 'positive'
        }
      } else {
        return {
          label: 'Linked no Updates',
          icon: 'fa fa-link-horizontal-slash',
          color: 'info'
        }
      }
    },
    linkContent () {
      if (this.hasLibraryParent) {
        return {
          label: 'Unlink from Library',
          icon: 'fa fa-cloud-slash',
          tooltip: `Disconnect this ${this.libraryLinkModel.humanTypeName} from the Library. It will no longer receive Library updates.`
        }
      } else {
        return {
          label: 'Link to Library',
          icon: 'fa fa-cloud-arrow-up',
          tooltip: `Connect this ${this.libraryLinkModel.humanTypeName} to the Library. It will receive automatic updates from the Library.`
        }
      }
    },
    libraryLinkAction () {
      if (this.updateLibraryParent) {
        return 'edit-link'
      } else if (this.hasLibraryParent && this.receiveLibraryUpdates) {
        return 'enable-updates'
      } else if (this.hasLibraryParent) {
        return 'disable-updates'
      } else {
        return 'none'
      }
    },
    canCreateLibraryModel () {
      if (_.isNil(this.libraryLinkModel)) return false

      if (this.collection.isSubmodel) return false

      // If the model is configured at the Circuit Level, or the Current Site is the Library, or the Current Circuit has no Library Site
      // Then the effective Library Site is the Region Library
      // We don't want random Admins to be able to create Library Models that could then be used by other Circuits
      // Superusers and CB Admins have Region Library access
      if (this.libraryLinkModel.level === MODEL_LEVEL.CIRCUIT || this.currentSite.isLibrary || !_.some(this.currentCircuit.sites.data, 'isLibrary')) {
        return _.some(this.currentUser.authorizedCircuits.data, 'isLibrary')
      } else {
        // If the effective Library Site is the Circuit Library, then the Admin can create Library Models if necessary
        return true
      }
    },
    canSaveChanges () {
      // We are updating the library parent, enabling updates (Creating Link), and have selected a library parent
      if (this.updateLibraryParent && this.receiveLibraryUpdates && !_.isNil(this.libraryParentId)) return true

      // We are updating the library parent, disabling updates (Destroying Link)
      if (this.updateLibraryParent && !this.receiveLibraryUpdates) return true

      // We are enabling or disabling updates
      if (!this.updateLibraryParent && this.updateReceiveLibraryUpdates) return true

      return false
    },
    dataLoading () {
      return this.loading || this.libraryLinkModel.loading || this.collection.loading
    }
  },
  created () {
    _.extend(this, { CREATE_NEW_LIBRARY_MODEL_ID })
  },
  methods: {
    reset () {
      this.loading = false
      this.updateLibraryParent = false
      this.updateReceiveLibraryUpdates = false
      this.libraryParentId = this.libraryLinkModel.libraryParentId
      this.receiveLibraryUpdates = this.libraryLinkModel.receiveLibraryUpdates

      // If the model does not have a library parent, default select the Create Link option
      if (_.isNil(this.libraryParentId)) {
        this.createLibraryLink()
      }
    },
    editLibraryLink () {
      if (this.hasLibraryParent) {
        this.destroyLibraryLink()
      } else {
        this.createLibraryLink()
      }
    },
    createLibraryLink () {
      this.updateLibrarySearchString(this.libraryLinkModelName)
      this.updateLibraryParent = true
      this.updateReceiveLibraryUpdates = true
      this.receiveLibraryUpdates = true
    },
    destroyLibraryLink () {
      this.updateLibraryParent = true
      this.libraryParentId = null

      this.updateReceiveLibraryUpdates = true
      this.receiveLibraryUpdates = false
    },
    enableLibraryUpdates () {
      this.updateLibraryParent = false

      if (this.libraryLinkModel.receiveLibraryUpdates) {
        this.updateReceiveLibraryUpdates = false
        this.receiveLibraryUpdates = true
      } else {
        this.updateReceiveLibraryUpdates = true
        this.receiveLibraryUpdates = true
      }
    },
    disableLibraryUpdates () {
      this.updateLibraryParent = false

      if (!this.libraryLinkModel.receiveLibraryUpdates) {
        this.updateReceiveLibraryUpdates = false
        this.receiveLibraryUpdates = false
      } else {
        this.updateReceiveLibraryUpdates = true
        this.receiveLibraryUpdates = false
      }
    },
    updateLibraryLinkModel (libraryModelId) {
      if (this.libraryParentId === libraryModelId) {
        this.libraryParentId = null
      } else {
        this.libraryParentId = libraryModelId
      }
    },
    updateLibrarySearchString (value) {
      if (_.isNil(this.libraryFilter)) return

      this.librarySearchString = value
      this.libraryFilter(value)
    },
    async forceUpdate () {
      if (!this.hasLibraryParent) return

      try {
        this.loading = true
        await this.libraryLinkModel.syncLibrariable()
        if (this.collection.isSubmodel) {
          this.collection.parent.fetch(true)
        } else {
          this.collection.fetch(true)
        }
        this.$refs.libraryLinkCellMenu.hide()
      } finally {
        this.loading = false
      }
    },
    async save () {
      try {
        this.loading = true

        if (this.updateLibraryParent) {
          if (!_.isNil(this.libraryParentId) && this.receiveLibraryUpdates) {
            const finalLibraryParentId = this.libraryParentId > 0 ? this.libraryParentId : null
            await this.libraryLinkModel.addOrUpdateLibrariable(finalLibraryParentId)
          } else if (this.hasLibraryParent) {
            await this.libraryLinkModel.deleteLibrariable()
          }
        } else if (this.updateReceiveLibraryUpdates) {
          if (this.receiveLibraryUpdates) {
            await this.libraryLinkModel.enableLibrariable()
          } else {
            await this.libraryLinkModel.disableLibrariable()
          }
        }

        if (this.collection.isSubmodel) {
          this.collection.parent.fetch(true)
        } else {
          this.collection.fetch(true)
        }
        this.$refs.libraryLinkCellMenu.hide()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .library-link-cell .q-btn--outline:before {
    border-color: $grey-5;
  }
  .q-radio, .q-btn {
    &.disabled {
      opacity: 0.25 !important;
    }
  }
  .radio-label {
    font-size: 14px;
  }
  .radio-description {
    margin-left: 1.75rem;
    font-size: 12px;
  }
  .library-list-header {
    background-color: color-mix(in lab, transparent 90%, var(--q-primary));
  }
</style>
